import React, { useState } from 'react';
import Editor from '@monaco-editor/react';
import "../CodeConverter.css";
import { FaDownload, FaCompress, FaBrush, FaCopy, FaCheck, FaTrash } from "react-icons/fa";
import JsonEditorPanel from "../components/JsonEditorPanel";
import CommonSeoContent from "../components/CommonSeoContent";
import useEditorActions from "../utils/useEditorActions";

const JsonToC = () => {

  const [inputCode, setInputCode] = useState("");
  const [outputCode, setOutputCode] = useState("");

  // Import reusable actions
  const { handleDownload, handleClear } = useEditorActions(
    setInputCode,
    outputCode,
    setOutputCode
  );

  const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

  const generateCStructs = (name, obj, structs = []) => {
    let lines = [`typedef struct {`];

    for (const key in obj) {
      const value = obj[key];
      let fieldName = key;
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        const nestedName = `${name}_${capitalize(key)}`;
        generateCStructs(nestedName, value, structs);
        lines.push(`  struct ${nestedName} ${fieldName};`);
      } else {
        const type = inferCType(value);
        lines.push(`  ${type} ${fieldName};`);
      }
    }

    lines.push(`} ${name};`);
    structs.unshift(lines.join('\n'));
    return structs;
  };

  const inferCType = (value) => {
    if (typeof value === 'number') return Number.isInteger(value) ? 'int' : 'float';
    if (typeof value === 'boolean') return 'bool';
    if (typeof value === 'string') return 'char*';
    return 'void*';
  };

  const handleConvert = () => {
    try {
      const json = JSON.parse(inputCode);
      if (typeof json !== 'object') throw new Error('Root must be an object');
      const structs = generateCStructs('MyStruct', json);
      setOutputCode(structs.join('\n\n'));
    } catch (e) {
      setOutputCode("// ❌ Invalid JSON:\n" + e.message);
    }
  };

  return (
    <div className="formatter-container">
      <h1>JSON ➝ C Converter </h1>

      {/* Toolbar */}
      <div className="toolbar">
        <button onClick={handleConvert} className="btn primary">
          <FaBrush /> Convert
        </button>
        <button onClick={() => handleDownload("c", "formatted-c")} className="btn success">
          <FaDownload /> Download
        </button>
		<button onClick={handleClear} className="btn danger">
          <FaTrash /> Clear
        </button>

      </div>

      {/* Editors */}
      <div className="editor-panels">
        <JsonEditorPanel title="Input JSON" language="json" code={inputCode} setCode={setInputCode} />
        <div className="editor-section-empty-div">
        </div>
        <JsonEditorPanel title="Output C" language="c"  code={outputCode} />
      </div>
	  
	  <CommonSeoContent
  title="Online JSON to C Struct Converter"
  description="Convert JSON data into C structs with jsonconverter.org. Supports nested JSON and generates clean C code with primitive types and pointers."
  keywords="JSON to C, json to c struct, json to c code, json to c generator, c struct converter, jsonconverter.org"
  author="jsonconverter.org"
  heading="Why Use JSON to C Converter?"
  whyUseText="Our JSON to C Converter helps you convert structured JSON data into C `struct` definitions — ideal for embedded systems, firmware development, or low-level C programming. It automatically detects the correct C data types, supports nested objects through nested structs, and handles arrays with pointer syntax or fixed sizes. This tool is perfect for scenarios where you need to map JSON into static memory models. Everything runs client-side, so your data never leaves your device — offering a fast, secure, and developer-friendly experience."
  howToUseSteps={[
    "Paste your JSON into the editor.",
    "Click Convert to generate C structs.",
    "Review or modify the struct definitions.",
    "Copy or download the output for your C project.",
  ]}
  features={[
    "Generates C struct definitions from JSON",
    "Supports nested JSON objects via nested structs",
    "Handles arrays with pointers or fixed sizes",
    "Output is clean, readable, and ready to use",
    "100% client-side — secure and offline-capable",
    "Free to use with no registration required",
  ]}
  inputTitle="Input JSON:"
  inputExample={`{
  "name": "John",
  "age": 25,
  "isActive": true
}`}
  outputTitle="Output C:"
  outputExample={`typedef struct {
  char name[50];
  int age;
  bool isActive;
} User;`}
  callToAction="Start converting your JSON to C structs instantly — no signup required."
/>
    </div>
  );
};

export default JsonToC;